import type { JobRow } from "@gwrctl/api-client";

/**
 * Yayın hattının sayfada gösterilen durumu iş geçmişinden türetilir; ayrı bir
 * "uygulama durumu" tablosu yok. Sürüm dosyaları agent'ın makinesinde, sunucu onları
 * okuyamaz — en güncel bilgi son sürüm kontrolünün ya da son yükseltmenin sonucudur.
 */

export interface Versions {
  appJsonVersion?: string;
  appJsonVersionCode?: number;
  gradleVersionCode?: number;
  gradleVersionName?: string;
  runtimeVersion?: string;
  /** app.json runtimeVersion bir politika nesnesiyse (ör. appVersion). */
  runtimePolicy?: string;
}

export interface BumpResult {
  from?: { version?: string; versionCode?: number; runtimeVersion?: string };
  version: string;
  versionCode?: number;
  committed?: boolean;
  versions?: Versions;
}

export interface BuildResult {
  path: string;
  version?: string;
  versionCode?: number;
}

/** Sürüm dosyalarına dokunan ve sonucunda sürüm bilgisi taşıyan işler. */
const VERSION_SOURCES = new Set(["release.version.check", "release.version.bump"]);

/** İş listesi yeniden eskiye sıralı gelir; ilk eşleşen en yenisidir. */
export function latestSucceeded(jobs: readonly JobRow[] | undefined, type: string): JobRow | undefined {
  return jobs?.find((j) => j.type === type && j.status === "succeeded");
}

/** En güncel sürüm bilgisi: kontrol ya da yükseltme, hangisi daha yeniyse. */
export function currentVersions(
  jobs: readonly JobRow[] | undefined,
): { versions: Versions; source: JobRow } | undefined {
  const source = jobs?.find(
    (j) =>
      VERSION_SOURCES.has(j.type) &&
      j.status === "succeeded" &&
      (j.result as { versions?: Versions } | null)?.versions,
  );
  if (!source) return undefined;
  return { versions: (source.result as { versions: Versions }).versions, source };
}

/** Uygulamanın asıl sürümü: adı ve versionCode'u (gradle öncelikli, yoksa app.json). */
export function headline(v: Versions): { name?: string; code?: number } {
  return {
    name: v.gradleVersionName ?? v.appJsonVersion,
    code: v.gradleVersionCode ?? v.appJsonVersionCode,
  };
}

/**
 * Son yükseltme henüz derlenmediyse onu döndürür. Tekrar "Sürüm yükselt"e basmadan
 * önce gösterilir: yükseltme olmuş, ama sonraki adım (derleme) yapılmamış.
 */
export function pendingBump(jobs: readonly JobRow[] | undefined): BumpResult | undefined {
  const bump = latestSucceeded(jobs, "release.version.bump");
  if (!bump) return undefined;
  const build = latestSucceeded(jobs, "build.gradle");
  if (build && new Date(build.createdAt) > new Date(bump.createdAt)) return undefined;
  // Yükseltme panel dışında geri alındıysa "derlenmedi" uyarısı yanlış yönlendirir.
  if (bumpDrifted(bump, jobs)) return undefined;
  return bump.result as BumpResult;
}

/**
 * Yükseltmeden SONRA okunan sürüm, yükseltmenin sonucuyla uyuşmuyor mu? Sahibi
 * yükseltmeyi panel dışında geri aldığında hat eski "→ 1.2.0 · 5"i göstermesin.
 * Yalnızca daha yeni bir kontrol varsa bilinebilir; yoksa false.
 */
export function bumpDrifted(bump: JobRow, jobs: readonly JobRow[] | undefined): boolean {
  if (bump.type !== "release.version.bump" || bump.status !== "succeeded") return false;
  const current = currentVersions(jobs);
  if (!current || current.source.id === bump.id) return false;
  if (new Date(current.source.createdAt) <= new Date(bump.createdAt)) return false;
  const r = bump.result as BumpResult | null;
  if (!r) return false;
  const h = headline(current.versions);
  return (
    (h.name !== undefined && h.name !== r.version) ||
    (h.code !== undefined && r.versionCode !== undefined && h.code !== r.versionCode)
  );
}

/** Yükseltme geçmişi, yeniden eskiye. */
export function bumpHistory(jobs: readonly JobRow[] | undefined): { job: JobRow; result: BumpResult }[] {
  return (jobs ?? [])
    .filter((j) => j.type === "release.version.bump" && j.status === "succeeded" && j.result)
    .map((job) => ({ job, result: job.result as BumpResult }));
}

/**
 * Play API'nin track adları Play Console'dakilerle aynı değil: API'de "beta" açık
 * testtir, kapalı test "alpha"dır. Panel Console adlarını gösterir, API adını gönderir.
 */
export const TRACK_LABELS: Record<string, string> = {
  internal: "Dahili test",
  alpha: "Kapalı test",
  beta: "Açık test",
  production: "Üretim",
};

export function bumpSemver(current: string, kind: "patch" | "minor" | "major"): string {
  const [maj = 0, min = 0, pat = 0] = current.split(".").map((n) => Number.parseInt(n, 10) || 0);
  if (kind === "major") return `${maj + 1}.0.0`;
  if (kind === "minor") return `${maj}.${min + 1}.0`;
  return `${maj}.${min}.${pat + 1}`;
}

export function fmtVersion(name?: string, code?: number): string {
  return `${name ?? "?"}${code !== undefined ? ` · ${code}` : ""}`;
}

export function shortTime(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
  });
}

/** Yayın hattında bir adımın son çalıştırmasını tek satırda özetler. */
export function stepSummary(job: JobRow): string | undefined {
  if (job.status !== "succeeded" || !job.result) return undefined;
  const r = job.result as Record<string, unknown>;
  switch (job.type) {
    case "release.version.check": {
      const v = r.versions as Versions | undefined;
      const h = v ? headline(v) : undefined;
      const errors = r.errors as number | undefined;
      return `${h ? fmtVersion(h.name, h.code) : ""}${errors ? ` · ${errors} engel` : " · engel yok"}`;
    }
    case "release.plan":
      return r.recommendation === "ota" ? "OTA yeterli" : "native derleme gerekli";
    case "release.version.bump": {
      const b = r as unknown as BumpResult;
      return b.from
        ? `${fmtVersion(b.from.version, b.from.versionCode)} → ${fmtVersion(b.version, b.versionCode)}`
        : `→ ${fmtVersion(b.version, b.versionCode)}`;
    }
    case "build.gradle": {
      const b = r as unknown as BuildResult;
      return `AAB ${fmtVersion(b.version, b.versionCode)}`;
    }
    case "play.upload":
      return `versionCode ${String(r.versionCode ?? "?")}${r.track ? ` · ${TRACK_LABELS[String(r.track)] ?? String(r.track)}` : ""}`;
    default:
      return undefined;
  }
}

/** Hattın sırası tasarımdan geliyor: kontrol → analiz → yükseltme → derleme → yükleme. */
export const PIPELINE_JOBS = [
  "release.version.check",
  "release.plan",
  "release.version.bump",
  "build.gradle",
  "play.upload",
] as const;

export type PipelineJob = (typeof PIPELINE_JOBS)[number];

/**
 * Sıradaki önerilen adım. "ota" → değişiklik analizi OTA'nın yettiğini söyledi;
 * native hatta devam etmek gereksiz bir versionCode harcar.
 *
 * Kural: bekleyen yükseltme varsa derleme; yoksa en son BAŞARILI hat işinin bir
 * sonrası. Yükleme bittiyse yeni tur kontrolle başlar.
 */
export function recommendNext(jobs: readonly JobRow[] | undefined): PipelineJob | "ota" {
  if (pendingBump(jobs)) return "build.gradle";
  const last = jobs?.find(
    (j) => j.status === "succeeded" && (PIPELINE_JOBS as readonly string[]).includes(j.type),
  );
  if (!last) return "release.version.check";
  if (last.type === "release.plan") {
    return (last.result as { recommendation?: string } | null)?.recommendation === "ota"
      ? "ota"
      : "release.version.bump";
  }
  const i = PIPELINE_JOBS.indexOf(last.type as PipelineJob);
  // Yükseltme bekleyen değilse (geri alınmış / derlenmiş) yeniden kontrol gerekir.
  if (last.type === "release.version.bump") return "release.version.check";
  return PIPELINE_JOBS[i + 1] ?? "release.version.check";
}

/** İş süresi: "42 sn", "3 dk 12 sn", "1 sa 5 dk". */
export function fmtDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} sn`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} dk${s % 60 ? ` ${s % 60} sn` : ""}`;
  return `${Math.floor(m / 60)} sa${m % 60 ? ` ${m % 60} dk` : ""}`;
}

/** Bitmiş işte başlangıç→bitiş; süren işte şu ana kadar. Başlamadıysa boş. */
export function jobDuration(job: JobRow, now = Date.now()): string | undefined {
  if (!job.startedAt) return undefined;
  const end = job.finishedAt ? new Date(job.finishedAt).getTime() : now;
  return fmtDuration(end - new Date(job.startedAt).getTime());
}
